<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-building me-2"></i> {{ $t('company.title') }}
      </h4>

      <!-- Form -->
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('company.companyCode') }}</label>
              <input type="text" class="form-control" v-model="form.code" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('company.englishName') }}</label>
              <input type="text" class="form-control" v-model="form.english_name" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('company.arabicName') }}</label>
              <input type="text" class="form-control" v-model="form.arabic_name" />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('company.ceo') }}</label>
              <select v-model="form.gerant_id" class="form-select">
                <option value="" disabled>{{ $t('company.selectCeo') }}</option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.firstname }} {{ user.lastname }}
                </option>
              </select>
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded d-flex align-items-center justify-content-between">
              <label class="form-label mb-0">{{ $t('company.status') }}</label>
              <div class="form-check form-switch">
                <input type="checkbox" class="form-check-input" v-model="form.is_active" />
                <label class="form-check-label">{{ form.is_active ? $t('company.active') : $t('company.inactive') }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('company.update') : $t('company.save') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('company.cancel') }}</button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('company.existingCompanies') }}
      </h5>

      <div v-if="companies.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('company.code') }}</th>
              <th>{{ $t('company.englishName') }}</th>
              <th>{{ $t('company.arabicName') }}</th>
              <th>{{ $t('company.ceo') }}</th>
              <th>{{ $t('company.status') }}</th>
              <th class="text-center">{{ $t('company.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="company in companies" :key="company.id">
              <td>{{ company.code }}</td>
              <td>{{ company.english_name }}</td>
              <td>{{ company.arabic_name || '-' }}</td>
              <td>{{ getUserName(company.gerant_id) }}</td>
              <td>
                <span :class="company.is_active ? 'text-success' : 'text-danger'">
                  {{ company.is_active ? $t('company.active') : $t('company.inactive') }}
                </span>
              </td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadCompany(company)">{{ $t('company.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm me-2" @click="deleteCompany(company.id)">{{ $t('company.delete') }}</button>
                <button class="btn btn-outline-dark btn-sm" @click="toggleStatus(company.id)">{{ $t('company.toggle') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('company.noCompanies') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import companyService from '@/services/companyService'
import userService from '@/services/userService'

const toast = useToast()
const companies = ref([])
const users = ref([])
const editingId = ref(null)

const form = ref({
  code: '',
  english_name: '',
  arabic_name: '',
  is_active: true,
  gerant_id: null
})

const fetchCompanies = async () => {
  try {
    const { data } = await companyService.getAll()
    companies.value = data

    const ceoIds = [...new Set(data.map(c => c.gerant_id).filter(Boolean))]

    const existingUserIds = users.value.map(u => u.id)
    const missingIds = ceoIds.filter(id => !existingUserIds.includes(id))

    for (const id of missingIds) {
      try {
        const { data } = await userService.get(id)
        users.value.push(data)
      } catch {
       
      }
    }
  } catch (err) {
    toast.error($t('company.failedToLoadCompanies'))
  }
}

const fetchUsers = async () => {
  try {
    const { data } = await userService.getCeoUsers()
    users.value = data.data // format : { message, data: [...] }
  } catch (err) {
    toast.error($t('company.failedToLoadCeoUsers'))
  }
}

const getUserName = (id) => {
  const user = users.value.find(u => u.id === id)
  return user ? `${user.firstname} ${user.lastname}` : '-'
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await companyService.update(editingId.value, form.value)
      toast.success($t('company.companyUpdated'))
    } else {
      await companyService.create(form.value)
      toast.success($t('company.companyCreated'))
    }
    resetForm()
    fetchCompanies()
  } catch (err) {
    if (err.response?.status === 422) {
      const messages = Object.values(err.response.data.errors).flat().join('\n')
      toast.error(messages)
    } else {
      toast.error($t('company.errorSavingCompany'))
    }
  }
}

const loadCompany = (company) => {
  editingId.value = company.id
  form.value = {
    code: company.code,
    english_name: company.english_name,
    arabic_name: company.arabic_name,
    is_active: company.is_active,
    gerant_id: company.gerant_id
  }

  const alreadyExists = users.value.some(u => u.id === company.gerant_id)
  if (!alreadyExists && company.gerant_id) {
    userService.get(company.gerant_id).then(({ data }) => {
      users.value.push(data)
    }).catch(() => {
      toast.warning($t('company.ceoNotListed'))
    })
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    code: '',
    english_name: '',
    arabic_name: '',
    is_active: true,
    gerant_id: null
  }
}

const deleteCompany = async (id) => {
  if (!confirm($t('company.confirmDelete'))) return
  try {
    await companyService.delete(id)
    toast.success($t('company.companyDeleted'))
    fetchCompanies()
  } catch (err) {
    toast.error($t('company.failedToDeleteCompany'))
  }
}

const toggleStatus = async (id) => {
  try {
    await companyService.toggleStatus(id)
    const target = companies.value.find(c => c.id === id)
    if (target) target.is_active = !target.is_active
    toast.success($t('company.statusUpdated'))
  } catch (err) {
    toast.error($t('company.failedToUpdateStatus'))
  }
}

onMounted(() => {
  fetchUsers()
  fetchCompanies()
})
</script>
